import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const WeddingNavbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMusicPlaying, setIsMusicPlaying] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  

  useEffect(() => {
    // Create audio element
    audioRef.current = new Audio();
    audioRef.current.loop = true;
    audioRef.current.volume = 0.5;
    audioRef.current.src = '/background-music.mp3';
    audioRef.current.preload = 'auto';
    
    let hasStarted = false;
    
    // Function to start playing music
    const startMusic = async () => {
      if (!audioRef.current || hasStarted) return;
      
      try {
        await audioRef.current.play();
        audioRef.current.muted = false;
        setIsMusicPlaying(true);
        hasStarted = true;
      } catch (error) {
        // If play fails, it will be handled by interaction listeners
      }
    };
    
    // Function to enable audio on user interaction
    const enableAudio = async () => {
      if (audioRef.current && !hasStarted) {
        try {
          await startMusic();
          // Clean up listeners
          document.removeEventListener('click', enableAudio);
          document.removeEventListener('touchstart', enableAudio);
          document.removeEventListener('keydown', enableAudio);
          document.removeEventListener('mousemove', enableAudio);
          document.removeEventListener('scroll', enableAudio);
        } catch (error) {
          console.log('Could not play music:', error);
        }
      }
    };
    
    // Unmute function for when music is playing muted
    const unmuteMusic = () => {
      if (audioRef.current && audioRef.current.muted && !audioRef.current.paused) {
        audioRef.current.muted = false;
        setIsMusicPlaying(true);
        // Remove listeners
        document.removeEventListener('click', unmuteMusic);
        document.removeEventListener('touchstart', unmuteMusic);
        document.removeEventListener('keydown', unmuteMusic);
        document.removeEventListener('mousemove', unmuteMusic);
      }
    };
    
    // Try muted autoplay (browsers allow this)
    const tryAutoplay = async () => {
      if (!audioRef.current) return false;
      
      try {
        // Start muted (browsers allow muted autoplay)
        audioRef.current.muted = true;
        await audioRef.current.play();
        hasStarted = true;
        setIsMusicPlaying(true); // Show as playing even if muted
        
        // Add listeners to unmute on first user interaction
        document.addEventListener('click', unmuteMusic, { once: true });
        document.addEventListener('touchstart', unmuteMusic, { once: true });
        document.addEventListener('keydown', unmuteMusic, { once: true });
        document.addEventListener('mousemove', unmuteMusic, { once: true });
        
        return true;
      } catch (error) {
        // Muted autoplay failed, try unmuted
        try {
          audioRef.current.muted = false;
          await audioRef.current.play();
          setIsMusicPlaying(true);
          hasStarted = true;
          return true;
        } catch (error2) {
          // All autoplay failed, need user interaction
          return false;
        }
      }
    };
    
    // Set up audio ready handler
    const audio = audioRef.current;
    const handleReady = async () => {
      const success = await tryAutoplay();
      if (!success) {
        // Add listeners for user interaction to start playing
        document.addEventListener('click', enableAudio, { once: true });
        document.addEventListener('touchstart', enableAudio, { once: true });
        document.addEventListener('keydown', enableAudio, { once: true });
        document.addEventListener('mousemove', enableAudio, { once: true });
        document.addEventListener('scroll', enableAudio, { once: true, passive: true });
      }
    };
    
    // Try when audio is ready
    audio.addEventListener('canplaythrough', handleReady, { once: true });
    // Also try immediately (in case audio is already cached)
    handleReady();
    
    return () => {
      if (audio) {
        audio.removeEventListener('canplaythrough', handleReady);
        audio.pause();
      }
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
      document.removeEventListener('keydown', enableAudio);
      document.removeEventListener('mousemove', enableAudio);
      document.removeEventListener('scroll', enableAudio);
      document.removeEventListener('click', unmuteMusic);
      document.removeEventListener('touchstart', unmuteMusic);
      document.removeEventListener('keydown', unmuteMusic);
      document.removeEventListener('mousemove', unmuteMusic);
      if (audioRef.current) {
        audioRef.current = null;
      }
    };
  }, []);

  const toggleMusic = () => {
    if (!audioRef.current) return;

    if (isMusicPlaying) {
      audioRef.current.pause();
      setIsMusicPlaying(false);
    } else {
      audioRef.current.play().catch((error) => {
        console.log('Could not play music:', error);
      });
      setIsMusicPlaying(true);
    }
  };

  // Scroll to top when navigating
  const handleNavigation = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Noor', path: '/noor' },
    { name: 'Priya', path: '/priya' },
    // { name: 'Our Story', path: '/our-story' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Notes', path: '/love-notes' },
    { name: 'Games', path: '/games' },
    { name: 'Quiz', path: '/quiz' }
  
  ];

  return (
    <motion.nav
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-gradient-to-r from-white/95 via-rose-50/95 to-white/95 backdrop-blur-xl shadow-2xl border-b border-rose-200/50' 
          : 'bg-gradient-to-r from-transparent via-rose-50/20 to-transparent backdrop-blur-sm'
      }`}
    >
      {/* Decorative Background Elements - Simplified */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(2)].map((_, i) => (
          <motion.div
            key={`nav-heart-${i}`}
            className="absolute text-lg opacity-10"
            style={{ 
              left: `${30 + i * 40}%`, 
              top: '50%',
              transform: 'translateY(-50%)'
            }}
            animate={{
              y: [-3, -8, -3],
              opacity: [0.05, 0.15, 0.05],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              delay: i * 2,
              ease: "easeInOut",
            }}
          >
            💕
          </motion.div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex justify-between items-center py-3 md:py-3">
          {/* Enhanced Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
                <Link 
                  to="/" 
                  className="relative group"
                  onClick={handleNavigation}
                >
              <div className="text-2xl md:text-4xl font-display font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-violet-600 bg-clip-text text-transparent drop-shadow-lg">
                Noor & Priya
              </div>
              {/* Decorative underline */}
              <motion.div
                className="absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-rose-400 to-pink-400"
                initial={{ width: 0 }}
                whileHover={{ width: "100%" }}
                transition={{ duration: 0.3 }}
              />
              {/* Sparkle effect on hover */}
              <motion.div
                className="absolute -top-2 -right-2 text-lg opacity-0 group-hover:opacity-100"
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              >
                ✨
              </motion.div>
            </Link>
          </motion.div>

                  {/* Enhanced Desktop Navigation */}
                  <div className="hidden md:flex items-center space-x-2">
                    {navItems.map((item, index) => (
                      <motion.div
                        key={item.path}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                      >
                <Link
                  to={item.path}
                  className="relative group px-4 py-2 rounded-full transition-all duration-300"
                  onClick={handleNavigation}
                >
                  {/* Background on hover */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-rose-100 to-pink-100 rounded-full opacity-0 group-hover:opacity-100"
                    transition={{ duration: 0.3 }}
                  />
                  
                  {/* Active state background */}
                  {location.pathname === item.path && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full shadow-lg"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                  
                  <span className={`relative z-10 font-medium transition-colors duration-300 ${
                    location.pathname === item.path
                      ? 'text-white font-bold'
                      : 'text-slate-700 group-hover:text-rose-600'
                  }`}>
                    {item.name}
                  </span>
                  
                  {/* Decorative emoji on hover */}
                  <motion.div
                    className="absolute -top-1 -right-1 text-xs opacity-0 group-hover:opacity-100"
                    initial={{ scale: 0 }}
                    whileHover={{ scale: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                            {item.name === 'Home' && '🏠'}
                            {item.name === 'Our Story' && '📖'}
                            {item.name === 'Gallery' && '📸'}
                            {item.name === 'Notes' && '💌'}
                            {item.name === 'Games' && '🎮'}
                            {item.name === 'Quiz' && '🧠'}
                            {item.name === 'Noor' && '👨‍💻'}
                            {item.name === 'Priya' && '👩‍⚕️'}
                  </motion.div>
                </Link>
              </motion.div>
            ))}
            
            {/* Music Toggle Button */}
            <motion.button
              onClick={toggleMusic}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="ml-4 p-2 md:p-3 transition-all duration-300 hover:shadow-xl hover:from-rose-200 hover:to-pink-200"
              title={isMusicPlaying ? 'Music On' : 'Music Off'}
            >
              <motion.span 
                className="text-lg md:text-xl"
                animate={{ rotate: isMusicPlaying ? 360 : 0 }}
                transition={{ duration: 2, repeat: isMusicPlaying ? Infinity : 0, ease: "linear" }}
              >
                {isMusicPlaying ? '🎵' : '🔇'}
              </motion.span>
            </motion.button>
          </div>

          {/* Enhanced Mobile menu button */}
          <motion.button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="md:hidden relative p-2 rounded-full bg-gradient-to-r from-rose-100 to-pink-100 shadow-lg transition-all duration-300 hover:shadow-xl"
          >
            <motion.div
              animate={{ rotate: isMobileMenuOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <svg
                className="w-5 h-5 text-slate-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </motion.div>
            {/* Decorative hearts around button */}
            {isMobileMenuOpen && (
              <>
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="absolute -top-1 -right-1 text-xs"
                >
                  💕
                </motion.div>
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.1 }}
                  className="absolute -bottom-1 -left-1 text-xs"
                >
                  💖
                </motion.div>
              </>
            )}
          </motion.button>
        </div>

        {/* Enhanced Mobile Navigation */}
                <motion.div
                  initial={false}
                  animate={{
                    height: isMobileMenuOpen ? 'auto' : 0,
                    opacity: isMobileMenuOpen ? 1 : 0,
                  }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="md:hidden overflow-hidden bg-gradient-to-br from-white/95 via-rose-50/95 to-pink-50/95 backdrop-blur-xl rounded-2xl mb-4 shadow-2xl border border-rose-200/50"
                >
          <div className="py-6 px-4 space-y-3">
            {navItems.map((item, index) => (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, x: -10 }}
                animate={isMobileMenuOpen ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
              >
                <Link
                  to={item.path}
                  onClick={handleNavigation}
                  className="group relative flex items-center px-4 py-3 text-lg font-medium rounded-xl transition-all duration-300"
                >
                  {/* Background states */}
                  <motion.div
                    className={`absolute inset-0 rounded-xl transition-all duration-300 ${
                      location.pathname === item.path
                        ? 'bg-gradient-to-r from-rose-500 to-pink-500 shadow-lg'
                        : 'bg-gradient-to-r from-rose-100 to-pink-100 opacity-0 group-hover:opacity-100'
                    }`}
                  />
                  
                  {/* Emoji icon */}
                  <motion.span
                    className="relative z-10 mr-3 text-xl"
                    animate={{ 
                      rotate: location.pathname === item.path ? [0, 10, -10, 0] : 0 
                    }}
                    transition={{ duration: 0.5 }}
                  >
                            {item.name === 'Home' && '🏠'}
                            {item.name === 'Our Story' && '📖'}
                            {item.name === 'Gallery' && '📸'}
                            {item.name === 'Notes' && '💌'}
                            {item.name === 'Games' && '🎮'}
                            {item.name === 'Quiz' && '🧠'}
                            {item.name === 'Noor' && '👨‍💻'}
                            {item.name === 'Priya' && '👩‍⚕️'}
                  </motion.span>
                  
                  {/* Text */}
                  <span className={`relative z-10 transition-colors duration-300 ${
                    location.pathname === item.path
                      ? 'text-white font-bold'
                      : 'text-slate-700 group-hover:text-rose-600'
                  }`}>
                    {item.name}
                  </span>
                  
                  {/* Active indicator */}
                  {location.pathname === item.path && (
                    <motion.div
                      layoutId="mobileActiveTab"
                      className="absolute right-4 w-2 h-2 bg-white rounded-full"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                </Link>
              </motion.div>
            ))}
            
            {/* Music Toggle Button for Mobile */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={isMobileMenuOpen ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
              transition={{ duration: 0.3, delay: navItems.length * 0.1 }}
              className="pt-4 border-t border-rose-200/50"
            >
              <motion.button
                onClick={toggleMusic}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-rose-100 to-pink-100 rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl hover:from-rose-200 hover:to-pink-200"
              >
                <motion.span 
                  className="text-xl mr-3"
                  animate={{ rotate: isMusicPlaying ? 360 : 0 }}
                  transition={{ duration: 2, repeat: isMusicPlaying ? Infinity : 0, ease: "linear" }}
                >
                  {isMusicPlaying ? '🎵' : '🔇'}
                </motion.span>
                <span className="text-slate-700 font-medium">
                  {isMusicPlaying ? 'Music On' : 'Music Off'}
                </span>
              </motion.button>
            </motion.div>
            
            {/* Decorative bottom element */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={isMobileMenuOpen ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              className="flex justify-center pt-4"
            >
              <div className="flex items-center gap-2 text-rose-400">
                <motion.span
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                >
                  ✨
                </motion.span>
                <span className="text-sm font-medium">Made with Love</span>
                <motion.span
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                >
                  ✨
                </motion.span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.nav>
  );
};

export default WeddingNavbar;
